/**
 * NCC: "Add a drink" (blocks/ncc-addon-picker.liquid).
 *
 * The select belongs to the section's product form (`name="ncc_addon"`); assets/product-form.js (NCC edit) turns a
 * chosen drink into a second line of the same add-to-bag request. This element only keeps the page honest: while
 * a drink is chosen the price block and the sticky bar show the product + drink price (each option carries it,
 * rendered by Liquid), "No drink" puts the product's price back, and a variant change refreshes the options'
 * prices from the section markup Horizon fetched (like the plan picker). The choice stays after an add, like a
 * variant choice does.
 */

import { StandardEvents } from '@shopify/events';

class AddonPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#apply();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @returns {Element | null} */
  get #section() {
    return this.closest('.shopify-section');
  }

  /** @returns {HTMLSelectElement | null} */
  get #select() {
    return this.querySelector('select[name="ncc_addon"]');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target === this.#select) this.#apply();
  };

  /**
   * A variant change re-renders the price block; the fetched markup carries this block rendered for the new
   * variant, so the options' totals are copied from it before the price is written again.
   * @param {CustomEvent & { promise?: Promise<{ detail?: { html?: Document | Element } }> }} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector('ncc-addon-picker');
        if (fresh) {
          for (const option of this.querySelectorAll('option')) {
            const source = fresh.querySelector(`option[value="${option.value}"]`);
            if (source instanceof HTMLOptionElement) option.dataset.price = source.dataset.price || '';
          }
        }
        window.setTimeout(() => this.#apply(), 0);
      })
      .catch(() => {});
  };

  #apply() {
    if (!this.hasAttribute('data-show-total')) return;
    const option = this.#select?.selectedOptions[0];
    const price = option?.dataset.price;
    if (!price) return;
    const section = this.#section || document;
    for (const target of section.querySelectorAll('product-price .price, .sticky-add-to-cart__price .price')) {
      if (target.querySelector('.price')) continue; // a wrapper around the real price span
      if (target.textContent !== price) target.textContent = price;
    }
  }
}

if (!customElements.get('ncc-addon-picker')) customElements.define('ncc-addon-picker', AddonPicker);
